import { httpClient } from "@/lib/httpClient";

export interface SectionWithAccess {
  id: string;
  slug: string;
  title: string;
  ordinal: number;
  /** Role uuids allowed to see this section; empty = visible to all staff. */
  roleIds: string[];
}

export interface PageWithAccess {
  id: string;
  slug: string;
  title: string;
  ordinal: number;
  /** The parent page, or null at top level. Absent from an older server. */
  parentPageId?: string | null;
  /** Role uuids that further restrict this page; empty = follows the section. */
  roleIds: string[];
}

export const sectionAccessService = {
  /** Every live section, unfiltered by the caller's own visibility. */
  listSections: () => httpClient.get<{ sections: SectionWithAccess[] }>("/api/cms/sections"),

  /** Create a section at the end of the tree; returns the fresh full list. */
  createSection: (slug: string, title: string, csrfToken: string) =>
    httpClient.post<{ sections: SectionWithAccess[] }>("/api/cms/sections", {
      json: { slug, title },
      headers: { "x-csrf-token": csrfToken },
    }),

  /** Retitle a section; its slug (and page URLs) stay stable. */
  renameSection: (sectionId: string, title: string, csrfToken: string) =>
    httpClient.put<{ ok: true }>(`/api/cms/sections/${sectionId}`, {
      json: { title },
      headers: { "x-csrf-token": csrfToken },
    }),

  /** Delete an empty section (409 while pages remain); returns the fresh list. */
  deleteSection: (sectionId: string, csrfToken: string) =>
    httpClient.delete<{ sections: SectionWithAccess[] }>(`/api/cms/sections/${sectionId}`, {
      headers: { "x-csrf-token": csrfToken },
    }),

  /** Delete a page, published or not — structural admin, audited server-side. */
  deletePage: (pageId: string, csrfToken: string) =>
    httpClient.delete<{ ok: true }>(`/api/cms/pages/${pageId}`, {
      headers: { "x-csrf-token": csrfToken },
    }),

  /** Replace a section's allowed-role list. Empty array = unrestricted. */
  setAccess: (sectionId: string, roleIds: string[], csrfToken: string) =>
    httpClient.put<{ ok: true }>(`/api/cms/sections/${sectionId}/access`, {
      json: { roleIds },
      headers: { "x-csrf-token": csrfToken },
    }),

  /** Replace the whole section order; ordinal is reassigned 1..N server-side. */
  reorder: (order: string[], csrfToken: string) =>
    httpClient.put<{ sections: SectionWithAccess[] }>("/api/cms/sections/order", {
      json: { order },
      headers: { "x-csrf-token": csrfToken },
    }),

  /** Every live page in a section, unfiltered by the caller's own visibility. */
  listPages: (sectionId: string) =>
    httpClient.get<{ pages: PageWithAccess[] }>(`/api/cms/sections/${sectionId}/pages`),

  /** Replace a page's allowed-role list. Empty array = follows the section. */
  setPageAccess: (pageId: string, roleIds: string[], csrfToken: string) =>
    httpClient.put<{ ok: true }>(`/api/cms/pages/${pageId}/access`, {
      json: { roleIds },
      headers: { "x-csrf-token": csrfToken },
    }),

  /** Replace a section's page order; ordinal is reassigned 1..N server-side. */
  reorderPages: (sectionId: string, order: string[], csrfToken: string) =>
    httpClient.put<{ pages: PageWithAccess[] }>(`/api/cms/sections/${sectionId}/pages/order`, {
      json: { order },
      headers: { "x-csrf-token": csrfToken },
    }),
};
